import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CButtonGroup,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CInputGroup,
  CInputGroupText,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import Empty from '../../../shared/Empty'
import SkeletonList from '../../../shared/SkeletonList'
import { icon } from '../../../shared/icons'
import { useUI } from '../../../shared/ui/UIProvider'
import { request } from '../../../services'
import { readSession } from '../../../services/session'

const statusColors = {
  1: 'success',
  0: 'secondary',
  true: 'success',
  false: 'secondary',
  draft: 'secondary',
  review: 'warning',
  approved: 'info',
  published: 'success',
  archived: 'dark',
  en_revision: 'warning',
  aprobado: 'info',
  rechazado: 'danger',
  publicado: 'success',
}

const select = (options) => ({ type: 'select', options })
const text = (rows = 3) => ({ type: 'textarea', rows })

const resources = [
  {
    id: 'health',
    label: 'Health',
    icon: 'cilHeartbeat',
    idField: 'status',
    readOnlyReason: 'Endpoint de diagnostico: solo consulta ping de /admin/ai.',
    columns: [
      { key: 'ok', label: 'OK', badge: true },
      { key: 'status', label: 'Status' },
      { key: 'db', label: 'DB' },
      { key: 'crypto', label: 'Cifrado' },
      { key: 'message', label: 'Mensaje' },
    ],
  },
  {
    id: 'prompts',
    label: 'Prompts',
    icon: 'cilNotes',
    idField: 'id',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canActivate: true,
    canDeactivate: true,
    filters: [{ key: 'agent', label: 'Agente' }],
    columns: [
      { key: 'agent', label: 'Agente' },
      { key: 'version', label: 'Version' },
      { key: 'isActive', label: 'Activo', badge: true },
      { key: 'comment', label: 'Comentario' },
      { key: 'createdUser', label: 'Usuario' },
    ],
    fields: [
      { key: 'agent', label: 'Agente', required: true },
      { key: 'version', label: 'Version', input: { type: 'number' }, required: true },
      { key: 'content', label: 'Contenido', input: text(8), required: true },
      { key: 'comment', label: 'Comentario' },
      { key: 'isActive', label: 'Activo', input: select([{ value: 'true', label: 'Si' }, { value: 'false', label: 'No' }]) },
      { key: 'createdUser', label: 'Usuario creador', required: true },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'cilSettings',
    idField: 'key',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    useUpsert: true,
    columns: [
      { key: 'key', label: 'Key' },
      { key: 'value', label: 'Valor' },
      { key: 'isSecret', label: 'Secreto', badge: true },
      { key: 'updatedUser', label: 'Usuario' },
    ],
    fields: [
      { key: 'key', label: 'Key', required: true },
      { key: 'value', label: 'Valor' },
      { key: 'isSecret', label: 'Secreto', input: select([{ value: 'false', label: 'No' }, { value: 'true', label: 'Si' }]) },
      { key: 'updatedUser', label: 'Usuario actualizador' },
    ],
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    icon: 'cilBook',
    idField: 'uuid',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canDeactivate: true,
    filters: [{ key: 'category', label: 'Categoria' }],
    columns: [
      { key: 'title', label: 'Titulo' },
      { key: 'category', label: 'Categoria' },
      { key: 'status', label: 'Estado', badge: true },
      { key: 'updatedUser', label: 'Usuario' },
    ],
    fields: [
      { key: 'uuid', label: 'UUID', required: true },
      { key: 'title', label: 'Titulo', required: true },
      { key: 'category', label: 'Categoria' },
      { key: 'content', label: 'Contenido', input: text(8), required: true },
      { key: 'tags', label: 'Tags JSON o separados por coma' },
      { key: 'status', label: 'Estado', input: select([{ value: '1', label: 'Activo' }, { value: '0', label: 'Inactivo' }]) },
      { key: 'createdUser', label: 'Usuario creador', required: true },
      { key: 'updatedUser', label: 'Usuario actualizador' },
    ],
  },
  {
    id: 'review-queue',
    label: 'Review Queue',
    icon: 'cilBellExclamation',
    idField: 'id',
    canDelete: true,
    canStatus: true,
    readOnlyReason: 'Alta y edicion omitidas: esos payloads incluyen dataJson, irJson, fichaMd y appJs.',
    filters: [
      { key: 'estado', label: 'Estado' },
      { key: 'scriptUuid', label: 'Script UUID' },
    ],
    columns: [
      { key: 'title', label: 'Titulo' },
      { key: 'estado', label: 'Estado', badge: true },
      { key: 'origin', label: 'Origen' },
      { key: 'scriptUuid', label: 'Script UUID' },
      { key: 'createdUser', label: 'Usuario' },
    ],
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    icon: 'cilHistory',
    idField: 'id',
    canCreate: true,
    canDelete: true,
    filters: [
      { key: 'entity', label: 'Entidad' },
      { key: 'entityId', label: 'Entity ID' },
    ],
    columns: [
      { key: 'entity', label: 'Entidad' },
      { key: 'entityId', label: 'Entity ID' },
      { key: 'action', label: 'Accion' },
      { key: 'actor', label: 'Actor' },
      { key: 'createdDate', label: 'Fecha' },
    ],
    fields: [
      { key: 'entity', label: 'Entidad', required: true },
      { key: 'entityId', label: 'Entity ID', required: true },
      { key: 'action', label: 'Accion', required: true },
      { key: 'detail', label: 'Detalle JSON', input: text(5) },
      { key: 'actor', label: 'Actor' },
    ],
  },
]

function makeId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `ai-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function currentUser() {
  return readSession().email || 'admin'
}

function fieldValue(item, key) {
  const value = item?.[key]
  if (value === undefined || value === null) return ''
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function defaultForm(def) {
  const user = currentUser()
  const base = {}
  ;(def.fields || []).forEach((field) => {
    base[field.key] = ''
  })
  if (def.idField === 'uuid') base.uuid = makeId()
  if (def.id === 'review-queue') base.id = makeId()
  if ('version' in base) base.version = '1'
  if ('isActive' in base) base.isActive = 'true'
  if ('isSecret' in base) base.isSecret = 'false'
  if ('status' in base) base.status = '1'
  if ('targetEnv' in base) base.targetEnv = 'staging'
  if ('origin' in base) base.origin = 'admin_v2'
  if ('createdUser' in base) base.createdUser = user
  if ('updatedUser' in base) base.updatedUser = user
  if ('actor' in base) base.actor = user
  return base
}

function formFromItem(def, item) {
  const form = defaultForm(def)
  ;(def.fields || []).forEach((field) => {
    form[field.key] = fieldValue(item, field.key)
  })
  return form
}

function parseMaybeJSON(value) {
  const textValue = String(value || '').trim()
  if (!textValue) return undefined
  if (textValue.startsWith('{') || textValue.startsWith('[')) return JSON.parse(textValue)
  return textValue.split(',').map((part) => part.trim()).filter(Boolean)
}

function normalizePayload(def, form) {
  const payload = {}
  ;(def.fields || []).forEach((field) => {
    const raw = form[field.key]
    if (raw === '' && !field.required) return
    if (field.key === 'version') payload[field.key] = Number(raw || 1)
    else if (field.key === 'status' && def.id === 'knowledge') payload[field.key] = Number(raw || 0)
    else if (field.key === 'isActive' || field.key === 'isSecret') payload[field.key] = String(raw) === 'true'
    else if (field.key === 'tags' || field.key === 'detail') payload[field.key] = parseMaybeJSON(raw)
    else payload[field.key] = raw
  })
  return payload
}

function badgeFor(value) {
  const text = value === true ? 'true' : value === false ? 'false' : String(value ?? '')
  return <CBadge color={statusColors[text] || statusColors[value] || 'secondary'}>{text || '-'}</CBadge>
}

function AIAdminView({ onError }) {
  const { toast, confirm } = useUI()
  const [resourceId, setResourceId] = useState('prompts')
  const [items, setItems] = useState([])
  const [filters, setFilters] = useState({})
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busyKey, setBusyKey] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({})
  const [message, setMessage] = useState('')
  const [detail, setDetail] = useState(null)
  const [auditOpen, setAuditOpen] = useState(false)
  const [auditForm, setAuditForm] = useState({})
  const [auditMessage, setAuditMessage] = useState('')

  const def = resources.find((item) => item.id === resourceId) || resources[0]

  async function load(nextFilters = filters) {
    setLoading(true)
    const res = def.id === 'health'
      ? await request('ai_admin_get', { params: { resource: 'health', action: 'ping' } })
      : await request('ai_admin_list', { params: { resource: def.id, ...nextFilters } })
    setLoading(false)
    if (!res.ok) {
      const detailMessage = res.data?.error || res.data?.message || 'No se pudo cargar AI Admin'
      if (onError) onError(detailMessage)
      toast(detailMessage, 'danger')
      return
    }
    setItems(def.id === 'health' ? [{ ok: res.ok, ...res.data }] : Array.isArray(res.data) ? res.data : [])
  }

  useEffect(() => {
    setFilters({})
    setSearch('')
  }, [resourceId])

  useEffect(() => {
    load({})
  }, [resourceId])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => JSON.stringify(item).toLowerCase().includes(term))
  }, [items, search])

  function openCreate() {
    setEditing(null)
    setForm(defaultForm(def))
    setMessage('')
    setModalOpen(true)
  }

  function openEdit(item) {
    setEditing(item)
    setForm(formFromItem(def, item))
    setMessage('')
    setModalOpen(true)
  }

  function setFormValue(key, value) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function openAudit(item = null) {
    setAuditForm({
      entity: def.id,
      entityId: item?.[def.idField] ? String(item[def.idField]) : '',
      action: '',
      detail: '{\n  "source": "admin_v2"\n}',
      actor: currentUser(),
    })
    setAuditMessage('')
    setAuditOpen(true)
  }

  function idValue(item = editing || form) {
    return item?.[def.idField]
  }

  async function submit(event) {
    event.preventDefault()
    for (const field of def.fields || []) {
      if (field.required && !String(form[field.key] || '').trim()) {
        setMessage(`Captura ${field.label}`)
        return
      }
    }

    let payload
    try {
      payload = normalizePayload(def, form)
    } catch (error) {
      setMessage(`JSON invalido: ${error.message}`)
      return
    }

    const body = { resource: def.id, data: payload }
    if (editing) body[def.idField] = idValue()

    setSaving(true)
    const action = editing ? 'ai_admin_update' : def.useUpsert ? 'ai_admin_upsert_setting' : 'ai_admin_create'
    const res = await request(action, { method: 'POST', body })
    setSaving(false)
    if (!res.ok) {
      setMessage(res.data?.error || res.data?.message || 'No se pudo guardar')
      return
    }
    setModalOpen(false)
    toast(`${def.label} guardado`, 'success')
    load()
  }

  async function submitAudit(event) {
    event.preventDefault()
    if (!auditForm.entity || !auditForm.entityId || !auditForm.action) {
      setAuditMessage('Captura entidad, entity ID y accion')
      return
    }
    let detailPayload
    try {
      detailPayload = parseMaybeJSON(auditForm.detail)
    } catch (error) {
      setAuditMessage(`JSON invalido: ${error.message}`)
      return
    }
    setSaving(true)
    const res = await request('ai_admin_register_action', {
      method: 'POST',
      body: {
        entity: auditForm.entity,
        entityId: auditForm.entityId,
        action: auditForm.action,
        detail: detailPayload,
        actor: auditForm.actor || currentUser(),
      },
    })
    setSaving(false)
    if (!res.ok) {
      setAuditMessage(res.data?.error || res.data?.message || 'No se pudo registrar la accion')
      return
    }
    setAuditOpen(false)
    toast('Accion registrada en audit-log', 'success')
    if (def.id === 'audit-log') load()
  }

  async function remove(item) {
    const label = item[def.idField] || item.title || item.key || item.id
    const ok = await confirm({
      title: `Eliminar ${def.label}`,
      body: `Vas a eliminar "${label}".`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
      icon: 'cilTrash',
    })
    if (!ok) return
    setBusyKey(`${def.id}:delete:${label}`)
    const res = await request('ai_admin_delete', { method: 'POST', body: { resource: def.id, [def.idField]: label } })
    setBusyKey('')
    if (!res.ok) {
      toast(res.data?.error || res.data?.message || 'No se pudo eliminar', 'danger')
      return
    }
    toast(`${def.label} eliminado`, 'success')
    load()
  }

  async function statusChange(item, next, action) {
    const label = item[def.idField]
    const body = { resource: def.id, [def.idField]: label, action, updatedUser: currentUser() }
    if (def.id === 'prompts' && item.agent) body.agent = item.agent
    if (def.id === 'review-queue') {
      body.action = 'estado'
      body.data = { estado: next, feedback: String(item.feedback || `Cambio de estado a ${next}`).trim() }
    }
    setBusyKey(`${def.id}:status:${label}:${next || action}`)
    const requestAction = action === 'activate' ? 'ai_admin_activate_prompt' : action === 'deactivate' ? 'ai_admin_deactivate' : 'ai_admin_status'
    const res = await request(requestAction, { method: 'POST', body })
    setBusyKey('')
    if (!res.ok) {
      toast(res.data?.error || res.data?.message || 'No se pudo cambiar el estado', 'danger')
      return
    }
    toast('Estado actualizado', 'success')
    load()
  }

  async function viewDetails(item) {
    const label = item[def.idField]
    setDetail({ title: `${def.label} ${label}`, body: item, loading: true })
    const res = def.id === 'health'
      ? await request('ai_admin_get', { params: { resource: 'health', action: 'ping' } })
      : await request('ai_admin_get', { params: { resource: def.id, [def.idField]: label } })
    setDetail({ title: `${def.label} ${label}`, body: res.ok ? res.data : item, loading: false })
    if (!res.ok) toast(res.data?.error || res.data?.message || 'No se pudo consultar el detalle', 'warning')
  }

  function applyFilters(event) {
    event.preventDefault()
    load(filters)
  }

  function clearFilters() {
    setFilters({})
    load({})
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>AI Admin</h1>
          <p>Administracion de endpoints AI reales de public-api-boston via /admin/ai.</p>
        </div>
        <CButtonGroup>
          <CButton color="light" onClick={() => load()}><CIcon icon={icon('cilReload')} /> Recargar</CButton>
          <CButton color="light" onClick={() => openAudit()}><CIcon icon={icon('cilHistory')} /> Registrar accion</CButton>
          {def.canCreate ? (
            <CButton color="success" className="btn-brand" onClick={openCreate}>
              <CIcon icon={icon('cilPlus')} /> Nuevo
            </CButton>
          ) : null}
        </CButtonGroup>
      </div>

      <CNav variant="pills" className="mb-3 ai-admin-tabs">
        {resources.map((item) => (
          <CNavItem key={item.id}>
            <CNavLink active={item.id === def.id} onClick={() => setResourceId(item.id)}>
              <CIcon icon={icon(item.icon)} className="me-2" />
              {item.label}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      {def.readOnlyReason ? <CAlert color="info">{def.readOnlyReason}</CAlert> : null}

      <CForm className="ai-admin-filter" onSubmit={applyFilters}>
        {(def.filters || []).map((filter) => (
          <div key={filter.key}>
            <CFormLabel>{filter.label}</CFormLabel>
            <CFormInput value={filters[filter.key] || ''} onChange={(event) => setFilters({ ...filters, [filter.key]: event.target.value })} />
          </div>
        ))}
        {(def.filters || []).length ? (
          <div className="ai-admin-filter-actions">
            <CButton color="light" type="submit">Filtrar</CButton>
            <CButton color="light" type="button" onClick={clearFilters}>Limpiar</CButton>
          </div>
        ) : null}
      </CForm>

      <CInputGroup className="search-wrap">
        <CInputGroupText><CIcon icon={icon('cilMagnifyingGlass')} /></CInputGroupText>
        <CFormInput placeholder={`Buscar en ${def.label}`} value={search} onChange={(event) => setSearch(event.target.value)} />
      </CInputGroup>

      {loading ? <SkeletonList /> : null}
      {!loading && filtered.length === 0 ? <Empty icon={icon(def.icon)} text={`No hay registros para ${def.label}.`} /> : null}

      {!loading && filtered.length ? (
        <div className="table-responsive scripts-table-wrap ai-admin-table-wrap">
          <CTable className="scripts-table ai-admin-table" hover align="middle">
            <CTableHead>
              <CTableRow>
                {(def.columns || []).map((column) => <CTableHeaderCell key={column.key}>{column.label}</CTableHeaderCell>)}
                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {filtered.map((item, index) => {
                const label = item[def.idField] || index
                return (
                  <CTableRow key={`${def.id}-${label}`}>
                    {(def.columns || []).map((column) => (
                      <CTableDataCell key={column.key}>
                        {column.badge ? badgeFor(item[column.key]) : <span className="ai-admin-cell">{fieldValue(item, column.key) || '-'}</span>}
                      </CTableDataCell>
                    ))}
                    <CTableDataCell className="text-end">
                      <div className="scripts-actions">
                        <CButton color="light" size="sm" title="Detalle" onClick={() => viewDetails(item)}>
                          <CIcon icon={icon('cilZoom')} />
                        </CButton>
                        <CButton color="light" size="sm" title="Registrar accion" onClick={() => openAudit(item)}>
                          <CIcon icon={icon('cilHistory')} />
                        </CButton>
                        {def.canEdit ? (
                          <CButton color="light" size="sm" title="Editar" onClick={() => openEdit(item)}>
                            <CIcon icon={icon('cilPencil')} />
                          </CButton>
                        ) : null}
                        {def.canActivate ? (
                          <CButton color="light" size="sm" title="Activar" disabled={busyKey.includes(`:${label}:`)} onClick={() => statusChange(item, null, 'activate')}>
                            <CIcon icon={icon('cilCheckCircle')} />
                          </CButton>
                        ) : null}
                        {def.canDeactivate ? (
                          <CButton color="light" size="sm" title="Desactivar" disabled={busyKey.includes(`:${label}:`)} onClick={() => statusChange(item, null, 'deactivate')}>
                            <CIcon icon={icon('cilBan')} />
                          </CButton>
                        ) : null}
                        {def.id === 'review-queue' ? ['en_revision', 'aprobado', 'rechazado', 'publicado'].map((estado) => (
                          <CButton key={estado} color="light" size="sm" title={`Estado ${estado}`} disabled={busyKey.includes(`:${label}:`)} onClick={() => statusChange(item, estado, 'estado')}>
                            {estado.slice(0, 1).toUpperCase()}
                          </CButton>
                        )) : null}
                        {def.canDelete ? (
                          <CButton color="light" size="sm" title="Eliminar" disabled={busyKey.includes(`:${label}:`)} onClick={() => remove(item)}>
                            {busyKey.includes(`:delete:${label}`) ? <CSpinner size="sm" /> : <CIcon icon={icon('cilTrash')} />}
                          </CButton>
                        ) : null}
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      ) : null}

      <CModal visible={modalOpen} onClose={() => setModalOpen(false)} size="lg" alignment="center" scrollable>
        <CForm onSubmit={submit}>
          <CModalHeader>
            <CModalTitle>{editing ? `Editar ${def.label}` : `Crear ${def.label}`}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {message ? <CAlert color="warning">{message}</CAlert> : null}
            {(def.fields || []).map((field) => (
              <div className="mb-3" key={field.key}>
                <CFormLabel>{field.label}</CFormLabel>
                {field.input?.type === 'textarea' ? (
                  <CFormTextarea rows={field.input.rows || 3} value={form[field.key] || ''} onChange={(event) => setFormValue(field.key, event.target.value)} required={field.required} />
                ) : field.input?.type === 'select' ? (
                  <CFormSelect value={form[field.key] || ''} onChange={(event) => setFormValue(field.key, event.target.value)} required={field.required}>
                    {field.input.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </CFormSelect>
                ) : (
                  <CFormInput type={field.input?.type || 'text'} value={form[field.key] || ''} onChange={(event) => setFormValue(field.key, event.target.value)} required={field.required} disabled={Boolean(editing && field.key === def.idField)} />
                )}
              </div>
            ))}
          </CModalBody>
          <CModalFooter>
            <CButton color="light" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</CButton>
            <CButton color="success" className="btn-brand" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>

      <CModal visible={Boolean(detail)} onClose={() => setDetail(null)} size="lg" alignment="center" scrollable>
        <CModalHeader>
          <CModalTitle>{detail?.title || 'Detalle'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormTextarea className="json-editor" rows={22} readOnly value={detail?.loading ? 'Cargando...' : JSON.stringify(detail?.body || {}, null, 2)} />
        </CModalBody>
        <CModalFooter>
          <CButton color="light" onClick={() => setDetail(null)}>Cerrar</CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={auditOpen} onClose={() => setAuditOpen(false)} alignment="center">
        <CForm onSubmit={submitAudit}>
          <CModalHeader>
            <CModalTitle>Registrar accion AI</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {auditMessage ? <CAlert color="warning">{auditMessage}</CAlert> : null}
            <div className="mb-3">
              <CFormLabel>Entidad</CFormLabel>
              <CFormInput value={auditForm.entity || ''} onChange={(event) => setAuditForm({ ...auditForm, entity: event.target.value })} required />
            </div>
            <div className="mb-3">
              <CFormLabel>Entity ID</CFormLabel>
              <CFormInput value={auditForm.entityId || ''} onChange={(event) => setAuditForm({ ...auditForm, entityId: event.target.value })} required />
            </div>
            <div className="mb-3">
              <CFormLabel>Accion</CFormLabel>
              <CFormInput value={auditForm.action || ''} onChange={(event) => setAuditForm({ ...auditForm, action: event.target.value })} required />
            </div>
            <div className="mb-3">
              <CFormLabel>Detalle JSON</CFormLabel>
              <CFormTextarea rows={5} value={auditForm.detail || ''} onChange={(event) => setAuditForm({ ...auditForm, detail: event.target.value })} />
            </div>
            <div>
              <CFormLabel>Actor</CFormLabel>
              <CFormInput value={auditForm.actor || ''} onChange={(event) => setAuditForm({ ...auditForm, actor: event.target.value })} />
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="light" onClick={() => setAuditOpen(false)} disabled={saving}>Cancelar</CButton>
            <CButton color="success" className="btn-brand" type="submit" disabled={saving}>
              {saving ? 'Registrando...' : 'Registrar'}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}

export default AIAdminView
